import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Package, 
  QrCode, 
  MapPin, 
  Phone, 
  CheckCircle, 
  AlertTriangle,
  Loader2,
  Navigation,
  Clock,
  CreditCard,
  Banknote,
  Euro,
  TrendingUp,
  PhoneCall
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import cashxDeliveryService from '@/services/cashxDeliveryService';
import QRScanner from '@/components/QRScanner';
import ShiftOverview from '@/components/ShiftOverview';
import TapToPayDialog from '@/components/TapToPayDialog';
import CashoutDialog from '@/components/CashoutDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Deliveries = () => {
  const { t } = useTranslation();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'active', 'completed'
  const [shift, setShift] = useState(null);
  const [showTapToPay, setShowTapToPay] = useState(false);
  const [showCashout, setShowCashout] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Get driver ID from localStorage
  const getDriverId = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.user_id || 'driver_zozo123';
  };

  const driverId = getDriverId();

  useEffect(() => {
    loadDeliveries();
    loadShiftData();
    
    const interval = setInterval(() => {
      loadDeliveries();
      loadShiftData();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadDeliveries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/drivers/${driverId}/deliveries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setDeliveries(data.deliveries || []);
    } catch (error) {
      console.error('Load deliveries error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadShiftData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/drivers/${driverId}/shift/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setShift(data);
    } catch (error) {
      console.error('Load shift error:', error);
    }
  };

  const handleScanSuccess = async (result) => {
    setShowScanner(false);
    toast.success('Lieferung übernommen!');
    loadDeliveries();
    loadShiftData();
    setActiveTab('active');
  };

  const handleCompleteDelivery = async (delivery, paymentMethod, tipAmount = 0) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/deliveries/${delivery.source_order_id}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_method: paymentMethod,
          amount_paid: delivery.total_gross,
          tip_amount: tipAmount
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Lieferung abgeschlossen! ✅');
        loadDeliveries();
        loadShiftData();
      }
    } catch (error) {
      toast.error('Fehler beim Abschließen');
    }
  };

  const handleTapToPay = (order) => {
    setSelectedOrder(order);
    setShowTapToPay(true);
  };

  const handlePaymentComplete = (payment) => {
    setShowTapToPay(false);
    loadDeliveries();
    loadShiftData();
  };

  const handleShiftStart = () => {
    loadShiftData();
    toast.success('Schicht gestartet! 🚗');
  };

  const handleShiftEnd = () => {
    setShowCashout(true);
  };

  const handleCashoutComplete = () => {
    setShowCashout(false);
    setShift(null);
    loadShiftData();
    setActiveTab('overview');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const activeDeliveries = deliveries.filter(d => ['claimed', 'in_transit'].includes(d.status));
  const completedDeliveries = deliveries.filter(d => ['delivered', 'completed'].includes(d.status));

  const STATUS_CONFIG = {
    pending: { label: 'Verfügbar', color: 'bg-green-500' },
    claimed: { label: 'Übernommen', color: 'bg-blue-500' },
    in_transit: { label: 'Unterwegs', color: 'bg-amber-500' },
    delivered: { label: 'Zugestellt', color: 'bg-green-600' },
    completed: { label: 'Abgeschlossen', color: 'bg-green-600' },
    problem: { label: 'Problem', color: 'bg-red-500' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 p-4 pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-white">Lieferungen</h1>
        <Button
          onClick={() => setShowScanner(true)}
          className="bg-primary hover:bg-primary/90"
          disabled={!shift?.active}
        >
          <QrCode className="h-5 w-5 mr-2" />
          QR Scannen
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        <Button
          onClick={() => setActiveTab('overview')}
          variant={activeTab === 'overview' ? 'default' : 'outline'}
          className={activeTab === 'overview' ? 'bg-primary' : 'border-zinc-700 text-zinc-400'}
          size="sm"
        >
          <TrendingUp className="h-4 w-4 mr-1" />
          Übersicht
        </Button>
        <Button
          onClick={() => setActiveTab('active')}
          variant={activeTab === 'active' ? 'default' : 'outline'}
          className={activeTab === 'active' ? 'bg-primary' : 'border-zinc-700 text-zinc-400'}
          size="sm"
        >
          <Package className="h-4 w-4 mr-1" />
          Aktiv
          {activeDeliveries.length > 0 && (
            <Badge className="ml-1 bg-red-500 text-white h-5 px-1.5">
              {activeDeliveries.length}
            </Badge>
          )}
        </Button>
        <Button
          onClick={() => setActiveTab('completed')}
          variant={activeTab === 'completed' ? 'default' : 'outline'}
          className={activeTab === 'completed' ? 'bg-primary' : 'border-zinc-700 text-zinc-400'}
          size="sm"
        >
          <CheckCircle className="h-4 w-4 mr-1" />
          Erledigt
          {completedDeliveries.length > 0 && (
            <Badge className="ml-1 bg-green-600 text-white h-5 px-1.5">
              {completedDeliveries.length}
            </Badge>
          )}
        </Button>
      </div>

      {/* Tab: Overview (Shift) */}
      {activeTab === 'overview' && (
        <ShiftOverview
          driverId={driverId}
          onShiftStart={handleShiftStart}
          onShiftEnd={handleShiftEnd}
        />
      )}

      {/* Tab: Active Deliveries */}
      {activeTab === 'active' && (
        <div className="space-y-4">
          {!shift?.active && (
            <Card className="bg-amber-900/30 border-amber-700">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                <div>
                  <p className="text-amber-200 font-medium">Keine aktive Schicht</p>
                  <p className="text-amber-300/70 text-sm">Starte zuerst eine Schicht in der Übersicht</p>
                </div>
              </CardContent>
            </Card>
          )}

          {activeDeliveries.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-8 text-center">
                <Package className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Keine aktiven Lieferungen</h3>
                <p className="text-zinc-400 mb-4">
                  Scanne einen QR-Code vom Lieferbon um eine Lieferung zu übernehmen
                </p>
                {shift?.active && (
                  <Button onClick={() => setShowScanner(true)} className="bg-primary">
                    <QrCode className="h-5 w-5 mr-2" />
                    QR-Code scannen
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            activeDeliveries.map(delivery => (
              <Card key={delivery.id || delivery.source_order_id} className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4 space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-white text-lg">
                          #{delivery.order_number}
                        </span>
                        <Badge className={STATUS_CONFIG[delivery.status]?.color || 'bg-zinc-600'}>
                          {STATUS_CONFIG[delivery.status]?.label || delivery.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-white">
                        {formatCurrency(delivery.total_gross)}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {delivery.payment_method === 'cash' ? '💵 Bar' : 
                         delivery.payment_method === 'card_online' ? '🌐 Online' : '💳 Karte'}
                      </p>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white">{delivery.customer_name}</span>
                      {delivery.customer_phone && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-600 text-green-500 hover:bg-green-600 hover:text-white h-8"
                          onClick={() => window.location.href = `tel:${delivery.customer_phone}`}
                        >
                          <PhoneCall className="h-4 w-4 mr-1" />
                          Anrufen
                        </Button>
                      )}
                    </div>

                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2 text-zinc-400">
                        <MapPin className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p>{delivery.delivery_address?.street} {delivery.delivery_address?.house_number}</p>
                          <p>{delivery.delivery_address?.postal_code} {delivery.delivery_address?.city}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-600 text-blue-500 hover:bg-blue-600 hover:text-white h-8"
                        onClick={() => {
                          const addr = delivery.delivery_address;
                          const fullAddress = `${addr?.street} ${addr?.house_number}, ${addr?.postal_code} ${addr?.city}`;
                          window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`, '_blank');
                        }}
                      >
                        <Navigation className="h-4 w-4 mr-1" />
                        Navi
                      </Button>
                    </div>
                  </div>

                  {/* Notes */}
                  {delivery.notes && (
                    <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded p-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-300">{delivery.notes}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    {delivery.payment_method === 'cash' && !delivery.payment_collected && (
                      <>
                        <Button
                          onClick={() => handleCompleteDelivery(delivery, 'cash', 0)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Banknote className="h-4 w-4 mr-1" />
                          Bar erhalten
                        </Button>
                        <Button
                          onClick={() => handleTapToPay(delivery)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          EC kassieren
                        </Button>
                      </>
                    )}

                    {delivery.payment_method !== 'cash' && !delivery.payment_collected && (
                      <Button
                        onClick={() => handleCompleteDelivery(delivery, delivery.payment_method, 0)}
                        className="col-span-2 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Zugestellt (bereits bezahlt)
                      </Button>
                    )}

                    {delivery.payment_method === 'card_onsite' && !delivery.payment_collected && (
                      <Button
                        onClick={() => handleTapToPay(delivery)}
                        className="col-span-2 bg-blue-600 hover:bg-blue-700"
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        EC kassieren
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Tab: Completed Deliveries */}
      {activeTab === 'completed' && (
        <div className="space-y-4">
          {completedDeliveries.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-8 text-center">
                <CheckCircle className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Keine erledigten Lieferungen</h3>
                <p className="text-zinc-400">
                  Erledigte Lieferungen erscheinen hier
                </p>
              </CardContent>
            </Card>
          ) : (
            completedDeliveries.map(delivery => (
              <Card key={delivery.id || delivery.source_order_id} className="bg-zinc-900 border-zinc-800 opacity-75">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="font-bold text-white">#{delivery.order_number}</span>
                      </div>
                      <p className="text-sm text-zinc-400">{delivery.customer_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-white">{formatCurrency(delivery.total_gross)}</p>
                      {delivery.payment_details?.tip_amount > 0 && (
                        <p className="text-xs text-amber-400">
                          +{formatCurrency(delivery.payment_details.tip_amount)} Trinkgeld
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* QR Scanner Modal */}
      {showScanner && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <QRScanner
              onScanSuccess={handleScanSuccess}
              onClose={() => setShowScanner(false)}
              title="QR-Code scannen"
              description="Scanne den QR-Code vom Lieferbon"
            />
          </div>
        </div>
      )}

      {/* Tap-to-Pay Dialog */}
      <TapToPayDialog
        open={showTapToPay}
        onClose={() => setShowTapToPay(false)}
        order={selectedOrder}
        onPaymentComplete={handlePaymentComplete}
      />

      {/* Cashout Dialog */}
      <CashoutDialog
        open={showCashout}
        onClose={() => setShowCashout(false)}
        shift={shift}
        driverId={driverId}
        onCashoutComplete={handleCashoutComplete}
      />
    </div>
  );
};

export default Deliveries;
